import { supabase } from '../../lib/supabase';

// Same `rpcUnknown` pattern used in the institute feature: until the user
// regenerates src/types/database.ts, the typed RPC union doesn't include the
// new Phase 14 RPCs. Narrow through `unknown` so this file is typecheck-clean.
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{
    data: unknown;
    error: { message: string } | null;
  }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

export type StudentSessionFilter = 'upcoming' | 'past' | 'all';
export type SessionStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
export type AttendanceStatus = 'invited' | 'present' | 'absent' | 'late' | 'excused';
export type AttemptStatus = 'in_progress' | 'submitted' | 'grading' | 'completed';
export type ExamType = 'academic' | 'general';

export interface StudentSession {
  session_id: string;
  institute_id: string;
  institute_name: string;
  test_id: string;
  test_title: string;
  exam_type: ExamType;
  scheduled_start: string;
  scheduled_end: string;
  location: string | null;
  status: SessionStatus;
  attendance_status: AttendanceStatus;
  attempt_id: string | null;
  attempt_status: AttemptStatus | null;
}

export interface StudentDashboardStats {
  scheduled_count: number;
  completed_count: number;
  in_progress_count: number;
  target_band: number | null;
}

export const STUDENT_QUERY_KEYS = {
  sessions: (filter: StudentSessionFilter) => ['student', 'sessions', filter] as const,
  nextSession: ['student', 'next_session'] as const,
  stats: ['student', 'stats'] as const,
};

export async function getStudentSessions(filter: StudentSessionFilter): Promise<StudentSession[]> {
  const { data, error } = await rpcUnknown.rpc('student_view_sessions', { p_filter: filter });
  if (error !== null) throw new Error(error.message);
  return (data as StudentSession[] | null) ?? [];
}

export async function getStudentNextSession(): Promise<StudentSession | null> {
  const { data, error } = await rpcUnknown.rpc('student_next_session');
  if (error !== null) throw new Error(error.message);
  const rows = (data as StudentSession[] | null) ?? [];
  return rows[0] ?? null;
}

export async function getStudentDashboardStats(): Promise<StudentDashboardStats> {
  const { data, error } = await rpcUnknown.rpc('student_dashboard_stats');
  if (error !== null) throw new Error(error.message);
  const rows = (data as StudentDashboardStats[] | null) ?? [];
  return (
    rows[0] ?? { scheduled_count: 0, completed_count: 0, in_progress_count: 0, target_band: null }
  );
}

// 15-minute pre-start window per Phase 14 spec. Caller passes the current
// time + session times so the check is testable without freezing the clock.
//
// Returns:
//   - 'before'    — still ≥ 15 min away
//   - 'open'      — within the join window OR session in progress
//   - 'after'     — scheduled_end already passed
//   - 'cancelled' — session cancelled
export type StartWindowState = 'before' | 'open' | 'after' | 'cancelled';

export interface StartWindowResult {
  state: StartWindowState;
  msUntilOpen: number; // 0 once we're 'open' or later
  msUntilClose: number; // 0 once we're 'after'
}

export function computeStartWindow(
  now: Date,
  session: Pick<StudentSession, 'scheduled_start' | 'scheduled_end' | 'status'>,
): StartWindowResult {
  if (session.status === 'cancelled') {
    return { state: 'cancelled', msUntilOpen: 0, msUntilClose: 0 };
  }
  const nowMs = now.getTime();
  const startMs = new Date(session.scheduled_start).getTime();
  const endMs = new Date(session.scheduled_end).getTime();
  const openMs = startMs - 15 * 60 * 1000;
  if (nowMs >= endMs) {
    return { state: 'after', msUntilOpen: 0, msUntilClose: 0 };
  }
  if (nowMs >= openMs) {
    return {
      state: 'open',
      msUntilOpen: 0,
      msUntilClose: Math.max(0, endMs - nowMs),
    };
  }
  return {
    state: 'before',
    msUntilOpen: Math.max(0, openMs - nowMs),
    msUntilClose: Math.max(0, endMs - nowMs),
  };
}
